// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { supabase, isPasswordRecoveryLoad } from "../lib/supabase";
import type { UserProfile } from "../types";
import { track, Events } from "../lib/analytics";

interface AuthState {
  user: UserProfile | null;
  emailVerified: boolean;
  loading: boolean;
  isPasswordRecovery: boolean;
  clearPasswordRecovery: () => void;
  signUp: (email: string, password: string, displayName: string) => Promise<{ error?: string; needsConfirmation?: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signInWithGoogle: () => Promise<{ error?: string }>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  updatePassword: (newPassword: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthState>({
  user: null,
  emailVerified: false,
  loading: true,
  isPasswordRecovery: false,
  clearPasswordRecovery: () => {},
  signUp: async () => ({ error: "Not initialized" }),
  signIn: async () => ({ error: "Not initialized" }),
  signInWithGoogle: async () => ({ error: "Not initialized" }),
  resetPassword: async () => ({ error: "Not initialized" }),
  updatePassword: async () => ({ error: "Not initialized" }),
  signOut: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function useAuthProvider(): AuthState {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  useEffect(() => {
    if (!supabase) { setLoading(false); return; }

    // Check current session on mount — clear bad/expired tokens gracefully.
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        supabase.auth.signOut();
        setLoading(false);
        return;
      }
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email || "",
          displayName: session.user.user_metadata?.display_name || session.user.email || "",
          avatarUrl: session.user.user_metadata?.avatar_url,
        });
        setEmailVerified(!!(session.user.email_confirmed_at));
      }
      // Show the reset-password form whenever ?recovery=1 is in the URL —
      // regardless of whether a session was established.  The check MUST be
      // outside `if (session?.user)`: if the token exchange fails for any
      // reason (flow-type mismatch, expired link, etc.) the session is null,
      // the block above is skipped, and the form would never appear.
      if (isPasswordRecoveryLoad) {
        setIsPasswordRecovery(true);
      }
      setLoading(false);
    });

    // Listen for auth changes.
    // PASSWORD_RECOVERY fires when Supabase completes the PKCE code exchange
    // from the reset email link. Because this is a network round-trip (~200ms),
    // our subscription is registered well before the event fires.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email || "",
            displayName: session.user.user_metadata?.display_name || session.user.email || "",
            avatarUrl: session.user.user_metadata?.avatar_url,
          });
          setEmailVerified(!!(session.user.email_confirmed_at));
        }
        setIsPasswordRecovery(true);
        setLoading(false);
        return;
      }
      if (event === "TOKEN_REFRESHED" && !session) {
        // Refresh failed — clear state
        setUser(null);
        setEmailVerified(false);
        return;
      }
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email || "",
          displayName: session.user.user_metadata?.display_name || session.user.email || "",
          avatarUrl: session.user.user_metadata?.avatar_url,
        });
        setEmailVerified(!!(session.user.email_confirmed_at));
        // On every login / session restore, claim any pending org invitations
        // for this user's email. Fire-and-forget — non-blocking.
        if (event === "SIGNED_IN" || event === "INITIAL_SESSION") {
          supabase.rpc("accept_my_pending_invitations").then(({ error }) => {
            if (error) console.warn("accept_my_pending_invitations:", error.message);
          });
        }
      } else {
        setUser(null);
        setEmailVerified(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = useCallback(async (email: string, password: string, displayName: string) => {
    if (!supabase) return { error: "Supabase not configured" };
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { display_name: displayName } },
    });
    if (error) return { error: error.message };
    track(Events.SIGNUP, { method: "email" });
    // Fire-and-forget welcome email (non-blocking)
    fetch("/api/send-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "welcome", email, displayName }),
    }).catch(() => {});
    // No session ⇒ Supabase requires email confirmation before login.
    // With autoconfirm enabled (current setting) a session is returned and
    // the user is already signed in — no "check your email" step exists.
    return { needsConfirmation: !data.session };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!supabase) return { error: "Supabase not configured" };
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    track(Events.LOGIN, { method: "email" });
    return {};
  }, []);

  const signInWithGoogle = useCallback(async () => {
    if (!supabase) return { error: "Supabase not configured" };
    // Pin redirectTo to this exact origin — prevents open-redirect attacks if
    // the Supabase dashboard allowlist ever has an accidental wildcard entry.
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/` },
    });
    if (error) return { error: error.message };
    return {};
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    if (!supabase) return { error: "Supabase not configured" };
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      // Append ?recovery=1 so we can detect this page load as a password-reset
      // BEFORE createClient() clears the URL.  This works for both PKCE links
      // (?code=xxx&recovery=1) and implicit links (#access_token=...&recovery=1).
      redirectTo: `${window.location.origin}/?recovery=1`,
    });
    if (error) return { error: error.message };
    return {};
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    if (!supabase) return { error: "Supabase not configured" };
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return { error: error.message };
    return {};
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setUser(null);
    setEmailVerified(false);
  }, []);

  const clearPasswordRecovery = useCallback(() => setIsPasswordRecovery(false), []);

  // emailVerified: always true when Supabase isn't configured (local/dev mode)
  return { user, emailVerified: !supabase ? true : emailVerified, loading, isPasswordRecovery, clearPasswordRecovery, signUp, signIn, signInWithGoogle, resetPassword, updatePassword, signOut };
}
